import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { SharedModule } from '@Shared/shared.module';
import { ChartModule, UIChart } from 'primeng/chart';

const CHART_COLORS = [
    '#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF', 
    '#FF9F40', '#7BC225', '#B325C2', '#C25B25'
];

export type AvgCaseCycleTimeLineChartData = {
    id: string;
    fullLabel: string;
    shortLabel: string;
    doneYear: number;
    doneMonth: number;
    avgCycleTime: number;
};

@Component({
  selector: 'avg-case-cycle-time-line-chart',
  templateUrl: './avg-case-cycle-time-line-chart.component.html',
  styleUrl: './avg-case-cycle-time-line-chart.component.scss',
  imports: [SharedModule, ChartModule]
})
export class AvgCaseCycleTimeLineChartComponent implements OnChanges {
    @ViewChild('chart') chart: UIChart;

    @Input() avgCaseCycleTime6Months: AvgCaseCycleTimeLineChartData[] = [];
    @Input() isExpanded = false;

    caseCycleTimeChartData: any;
    dataset: { id: string; fullLabel: string; shortLabel: string }[] = [];
    tooltipLabels: string[] = [];

    chartOptions = {
        animation: {
            duration: 0
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                mode: 'nearest',
                intersect: false,
                axis: 'xy',
                usePointStyle: true,
                caretPadding: 15,
                position: 'nearest',
                yAlign: (context: any) => {
                    if (!context.tooltip?.dataPoints?.[0]) return 'bottom';

                    const value = context.tooltip.dataPoints[0].raw;
                    const scale = context.chart.scales.y;
                    const midpoint = scale.min + (scale.max - scale.min) / 2;

                    return value > midpoint ? 'top' : 'bottom';
                },
                callbacks: {
                    title: (context: any) => {
                        const index = context[0]?.dataIndex;
                        return this.tooltipLabels[index];
                    },
                    label: (context: any) => {
                        const data = this.dataset.find(a => a.id === context.dataset.id);
                        const label = data.fullLabel;
                        const cycleTime = context.raw;
                        return label + ': ' + this.formatHoursToDayHour(cycleTime);
                    },
                    labelColor: (context: any) => {
                        const color = context.dataset.borderColor;
                        return {
                            borderColor: color,
                            backgroundColor: color
                        };
                    }
                }
            }
        },
        elements: {
            point: {
                pointStyle: 'circle',
                radius: (context: any) => {
                    const value = context.raw;
                    return value === 0 ? 0 : 4;
                },
                borderColor: (context: any) => {
                    return context.dataset.borderColor;
                },
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    font: {
                        size: 12
                    }
                }
            },
            y: {
                position: 'right',
                ticks: {
                    font: {
                        size: 12
                    },
                    precision: 0,
                    callback: (value: number) => {
                        if (value === 0) return '0';
                        return value + (value === 1 ? ' day' : ' days');
                    } 
                },
                border: {
                    display: false
                }
            }
        },
        onHover: (event: any, elements: any, chart: any) => {
            chart.data.datasets.forEach((dataset: any) => {
                if (!dataset.originalColor) {
                    dataset.originalColor = dataset.borderColor;
                }
            });

            if (elements && elements.length > 0) {
                const hoveredIndex = elements[0].datasetIndex;

                chart.data.datasets.forEach((dataset: any, index: number) => {
                    if (index === hoveredIndex) {
                        dataset.borderColor = dataset.originalColor;
                        dataset.borderWidth = 4;
                    } else {
                        dataset.borderColor = this.addOpacity(dataset.originalColor, 0.2);
                        dataset.borderWidth = 2;
                    }
                });
            } else {
                chart.data.datasets.forEach((dataset: any) => {
                    dataset.borderColor = dataset.originalColor || dataset.borderColor;
                    dataset.borderWidth = 2;
                });
            }

            chart.update('none');
        },
        interaction: {
            mode: 'dataset',
            intersect: false
        }
    };

    constructor() { }

    ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges['avgCaseCycleTime6Months'] && this.avgCaseCycleTime6Months) {
            this.getCaseDetails();
            this.updateChartData();
        }
        if (simpleChanges['isExpanded']) {
            this.updateLabelsForExpandState();
            setTimeout(() => {
                if (this.chart?.chart) {
                    this.chart.chart.resize();
                }
            }, 350);
        }
    }

    resetChart() {
        setTimeout(() => {
            const chart = this.chart?.chart;
            if (chart) {
                chart.data.datasets.forEach((dataset: any) => {
                    dataset.borderColor = dataset.originalColor || dataset.borderColor;
                    dataset.borderWidth = 2;
                });
                chart.update('none');
            }
        }, 100);
    }

    updateLabelsForExpandState(): void {
        this.caseCycleTimeChartData?.datasets?.forEach((dataset: any) => {
            const data = this.dataset.find(a => a.id === dataset.id);
            if (data) {
                dataset.label = this.isExpanded
                    ? data.fullLabel
                    : data.shortLabel;
            }
        });
    }

    updateChartData(): void {
        const monthBuckets = this.getLastSixMonthsBuckets();

        const dataMap = new Map<string, { fullLabel: string; shortLabel?: string; data: (number | null)[] }>();

        for (const entry of this.avgCaseCycleTime6Months) {
            if (!dataMap.has(entry.id)) {
                dataMap.set(entry.id, {
                    fullLabel: entry.fullLabel,
                    shortLabel: entry.shortLabel ?? entry.fullLabel.slice(0, 3).toUpperCase(),
                    data: new Array(6).fill(0)
                });
            }

            const bucketIndex = monthBuckets.findIndex(
                b => b.year === entry.doneYear && b.month === entry.doneMonth
            );

            if (bucketIndex !== -1) {
                dataMap.get(entry.id)!.data[bucketIndex] = entry.avgCycleTime / 24;
            }
        }

        const datasets = Array.from(dataMap.entries()).map(([id, data], index) => ({
            label: this.isExpanded ? data.fullLabel : data.shortLabel,
            id: id,
            fullLabel: data.fullLabel,
            shortLabel: data.shortLabel,
            data: data.data,
            borderColor: CHART_COLORS[index % CHART_COLORS.length],
            backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
            pointBackgroundColor: 'transparent',
            pointHoverBackgroundColor: 'transparent',
            borderWidth: 2,
            tension: 0.5,
            fill: false,
        }));

        const buckets = this.getLastSixMonthsBuckets();
        this.tooltipLabels = buckets.map(b => b.fullLabel);

        this.caseCycleTimeChartData = {
            labels: buckets.map(b => b.label),
            datasets
        };
    }

    getLastSixMonthsBuckets(): { year: number; month: number; label: string; fullLabel: string }[] {
        const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
        const now = new Date();
        return Array.from({ length: 6 }, (_, i) => {
            const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
            return {
                year: d.getFullYear(),
                month: d.getMonth() + 1,
                label: months[d.getMonth()],
                fullLabel: `${months[d.getMonth()]} ${d.getFullYear()}`
            };
        });
    }

    getCaseDetails() {
        const dataMap = new Map<string, { id: string; fullLabel: string; shortLabel: string }>();
        
        for (const entry of this.avgCaseCycleTime6Months) {
            if (!dataMap.has(entry.id)) {
                dataMap.set(entry.id, {
                    id: entry.id,
                    fullLabel: entry.fullLabel,
                    shortLabel: entry.shortLabel ?? entry.fullLabel.slice(0, 3).toUpperCase()
                });
            }
        }
        
        this.dataset = Array.from(dataMap.values());
    }

    formatHoursToDayHour(value: number): string {
        if (value === 0) return '0';
        let label = '';
        const hours = Math.ceil(value * 24);
        const days = Math.floor(hours / 24);
        if (days > 0) {
            label += days + (days === 1 ? ' day' : ' days');
        }
        const remainingHours = hours % 24;
        if (remainingHours > 0) {
            if (label) label += ' ';
            label += remainingHours + (remainingHours === 1 ? ' hour' : ' hours');
        }
        return label;
    }

    addOpacity(color: string, opacity: number): string {
        if (color.startsWith('#')) {
            const hex = color.replace('#', '');
            const r = Number.parseInt(hex.substring(0, 2), 16);
            const g = Number.parseInt(hex.substring(2, 4), 16);
            const b = Number.parseInt(hex.substring(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        }
        if (color.startsWith('rgb(')) {
            return color.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`);
        }
        if (color.startsWith('rgba(')) {
            return color.replace(/,\s*[\d.]+\)$/, `, ${opacity})`);
        }
        return color;
    }

    hasHiddenDatasets(): boolean {
        if (!this.chart?.chart) return false;
        return this.chart.chart.data.datasets.some((_: any, index: number) => !this.isDatasetVisible(index));
    }

    resetAllDatasets(): void {
        if (!this.chart?.chart) return;
        this.chart.chart.data.datasets.forEach((_: any, index: number) => {
            this.chart.chart.show(index);
        });
        this.chart.chart.update();
    }

    toggleDataset(index: number) {
        if (!this.chart?.chart) return;
        const chartInstance = this.chart.chart;
        const isHidden = chartInstance.getDatasetMeta(index).hidden;

        if (isHidden) {
            chartInstance.show(index);
        } else {
            chartInstance.hide(index);
        }
    }

    isDatasetVisible(index: number): boolean {
        if (!this.chart?.chart) return true;
        return !this.chart.chart.getDatasetMeta(index).hidden;
    }
}
